/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Center, Checkbox, Group, Loader, LoadingOverlay, Radio, Select, Stack, Text } from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { FC, useEffect, useState } from 'react';
import { IconCalendarEvent } from '@tabler/icons';
import { useNavigate } from 'react-router-dom';
import { DocumentS } from '../../../Templates/Documents/DocumentS';
import { EventS } from '../../../Service/EventS';
import { FormatS } from '../../../Service/FormatS';
import BookingEvent from '../../../Types/Event';
import { DataS } from '../../../Service/DataS';
import { ToastS } from '../../../Service/ToastS';
import { CreateContractRequest } from '../../../Types/DocumentT';
import EmptyText from '../../../Atoms/EmptyText';
import { DocumentDto, PositionDto } from '../../../Service/DocumentT';

export const openCreateContractModal = (props: CreateContractRequest) =>
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Vertrag erstellen
      </Text>
    ),
    children: <CreateContractModal {...props} />,
  });

interface AcceptOfferModalProps {
  bookitupDocument: DocumentDto;
  onAccept: () => void | Promise<void>;
}

export const openAcceptOfferModal = (props: AcceptOfferModalProps) =>
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Angebot annehmen
      </Text>
    ),
    children: <AcceptOfferModal {...props} />,
  });

const CreateContractModal: FC<CreateContractRequest> = ({ eventId, ...rest }) => {
  const [availableTemplateNames, setTemplatesNames] = useState<string[]>([]);
  const [template, selectTemplate] = useState<string>();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    DocumentS.getContractTemplatesNames()
      .then(setTemplatesNames)
      .finally(() => setLoading(false));
  }, []);

  const onConfirm = async () => {
    setLoading(true);
    const resJSON = await DocumentS.createContract({ eventId, ...rest, template });
    setLoading(false);
    if (resJSON) {
      navigate(`/events/${eventId}/contracts/${resJSON.id}`);
      closeAllModals();
    }
  };

  return (
    <>
      <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <Radio.Group
        withAsterisk
        spacing="xs"
        offset="md"
        orientation="vertical"
        value={template}
        onChange={selectTemplate}
        label="Vorlage auswählen:"
      >
        {availableTemplateNames.length === 0 && !loading && <EmptyText />}
        {availableTemplateNames.map((name) => (
          <Radio key={name} value={name} label={name} />
        ))}
      </Radio.Group>
      <Center mt={30}>
        <Button onClick={onConfirm} disabled={loading || !template} sx={{ width: 250 }}>
          Speichern
        </Button>
      </Center>
    </>
  );
};

const AcceptOfferModal: FC<AcceptOfferModalProps> = ({ bookitupDocument, onAccept }) => {
  const { id: documentId, positions, currency } = bookitupDocument;
  const [loading, setLoading] = useState(false);
  const requiredPositions = positions.filter((position) => !position.optional);
  const optionalPositions = positions.filter((position) => position.optional);
  const [selectedOptionalPositionIds, setSelectedOptionalPositionIds] = useState<number[]>(
    optionalPositions.filter((position) => position.autoSelect).map((position) => position.id),
  );

  const toggleOptionalPosition = (positionId: number) => {
    setSelectedOptionalPositionIds((current) =>
      current.includes(positionId) ? current.filter((id) => id !== positionId) : [...current, positionId],
    );
  };

  const acceptedTotal = positions.reduce((sum, position) => {
    if (!position.optional || selectedOptionalPositionIds.includes(position.id)) {
      return sum + position.price;
    }
    return sum;
  }, 0);

  const onConfirm = async () => {
    setLoading(true);
    const acceptedPositions = [...requiredPositions.map((position) => position.id), ...selectedOptionalPositionIds];
    const acceptedDocument = await DocumentS.acceptOffer(documentId, acceptedPositions);
    setLoading(false);
    if (acceptedDocument) {
      ToastS.success('document-accepted', 'Angebot angenommen.');
      await onAccept();
      closeAllModals();
    }
  };

  const renderPosition = (position: PositionDto) => (
    <Group key={position.id} position="apart" align="flex-start" noWrap>
      <div>
        <Text weight={500}>{position.label}</Text>
        <Text size="sm" color="dimmed">
          {FormatS.formatPriceWithCurrency(currency, position.price)}
        </Text>
        {position.subtext && (
          <Text size="sm" color="dimmed">
            {position.subtext}
          </Text>
        )}
      </div>
      {position.optional && (
        <Checkbox
          checked={selectedOptionalPositionIds.includes(position.id)}
          onChange={() => toggleOptionalPosition(position.id)}
          mt={2}
        />
      )}
    </Group>
  );

  return (
    <>
      <Text size="sm" color="dimmed" mb="md">
        Wähle aus, welche optionalen Positionen angenommen werden sollen.
      </Text>
      <Stack spacing="md">
        {positions.length === 0 && <EmptyText>Keine Positionen vorhanden.</EmptyText>}
        {positions.map(renderPosition)}
      </Stack>
      <Group position="apart" mt="xl">
        <Text weight={500}>Summe der angenommenen Positionen</Text>
        <Text weight="bolder">{FormatS.formatPriceWithCurrency(currency, acceptedTotal)}</Text>
      </Group>
      <Group position="right" spacing="xs" mt="xl">
        <Button variant="default" onClick={() => closeAllModals()}>
          Abbrechen
        </Button>
        <Button
          rightIcon={loading && <Loader size="xs" />}
          disabled={loading || positions.length === 0}
          onClick={onConfirm}
        >
          Angebot annehmen
        </Button>
      </Group>
    </>
  );
};

interface ChangeEventProps {
  bookitupDocument: DocumentDto;
  onChange: () => void;
}

// eslint-disable-next-line import/prefer-default-export
export const openAssignEventModal = (props: ChangeEventProps) =>
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Event zuordnen
      </Text>
    ),
    children: <AssignEventModal {...props} />,
  });

const AssignEventModal: FC<ChangeEventProps> = ({ bookitupDocument, onChange }) => {
  const { id: documentId, eventId: initialEventId, customerId } = bookitupDocument;
  const [loading, setLoading] = useState(false);
  const [selectedEvent, selectEvent] = useState<string | null>(null); // Let's use string for better usage with Select data
  const [bookingEvents, setBookingEvents] = useState<BookingEvent[]>([]);

  const { length } = bookingEvents;
  const emptyEvents = !loading && length === 0;
  const btnDisabled = initialEventId === Number(selectedEvent) || loading;

  useEffect(() => {
    setLoading(true);
    EventS.fetchEventsByCustomerParticipations(customerId)
      .then(setBookingEvents)
      .finally(() => setLoading(false));
  }, [customerId]);

  const onConfirm = async () => {
    if (selectedEvent) {
      setLoading(true);
      const patched = await DocumentS.patchDocument(documentId, { eventId: Number(selectedEvent) });
      setLoading(false);
      if (patched) {
        ToastS.success('foo', 'Event zugewiesen');
        onChange();
        closeAllModals();
      }
    }
  };

  return (
    <>
      <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <Select
        placeholder="Event wählen"
        icon={<IconCalendarEvent />}
        data={DataS.getEventOptions(bookingEvents)}
        value={selectedEvent}
        onChange={selectEvent}
        nothingFound={
          emptyEvents && (
            <Text>Es wurde kein Event zu diesem Kontakt gefunden. Bitte füge den Kontakt erst einem Event hinzu.</Text>
          )
        }
      />
      <Center mt="xl">
        <Button onClick={onConfirm} disabled={btnDisabled} sx={{ width: 250 }}>
          Speichern
        </Button>
      </Center>
    </>
  );
};
